import {
  Button,
  Checkbox,
  CustomSelect,
  LoadingArea,
  Pagination,
  Search,
  Spinner,
  type SelectOption,
} from '@consigli/facade';
import {
  apiUtil,
  useExportToExcelMutation,
  useGetVersionComparisonsSummaryQuery,
  useGetVersionComparisonsUniqueBlobsQuery,
  usePackage,
  usePackageId,
  useProjectId,
  useUpdateVersionComparisonReviewMutation,
  useVersionComparisons,
} from '@consigli/hooks';
import {
  type GetVersionComparisonsResponse,
  ReconciliationStatus,
  VersionCompareReviewStatus,
  type VersionComparison,
} from '@consigli/types';
import { MIME_TYPES, saveBlobBase64 } from '@consigli/utils';
import clsx from 'clsx';
import { FC, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiChevronDown, HiChevronRight, HiInformationCircle } from 'react-icons/hi';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { Breadcrumb } from '@/features/breadcrumb/breadcrumb';
import { WorkspaceProvider } from '@/layouts/workspace-layout/use-workspace-context';
import { WorkspaceLayout } from '@/layouts/workspace-layout/workspace-layout';

import { ReconciliationBadge } from './components/reconciliation-badge';
import { ReviewCell } from './components/review-cell';
import { type ContentSubGroup, groupComparisonsByPage } from './utils/group-findings';
import { createVersionCompareExcelExport } from './utils/version-compare-excel-export';
import { VersionComparePdfPanel } from './version-compare-pdf-panel';

import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';

const GROUPS_PER_PAGE = 10;
const SEARCH_DEBOUNCE_MS = 300;

enum ReconciliationTab {
  ALL = 'ALL',
  COMMENTS = 'COMMENTS',
  NO_COMMENTS = 'NO_COMMENTS',
  ADDRESSED = 'ADDRESSED',
  UNADDRESSED = 'UNADDRESSED',
  INDETERMINATE = 'INDETERMINATE',
}

// Tabs that only make sense when the analysis includes comments.
const COMMENT_RELATED_TABS: ReadonlySet<ReconciliationTab> = new Set([
  ReconciliationTab.COMMENTS,
  ReconciliationTab.ADDRESSED,
  ReconciliationTab.UNADDRESSED,
  ReconciliationTab.INDETERMINATE,
]);

const applyTabFilter = (item: VersionComparison, tab: ReconciliationTab): boolean => {
  switch (tab) {
    case ReconciliationTab.COMMENTS:
      return Boolean(item.commentText?.trim());
    case ReconciliationTab.NO_COMMENTS:
      return !item.commentText?.trim();
    case ReconciliationTab.ADDRESSED:
      return item.reconciliationStatus === ReconciliationStatus.ADDRESSED;
    case ReconciliationTab.UNADDRESSED:
      return item.reconciliationStatus === ReconciliationStatus.UNADDRESSED;
    case ReconciliationTab.INDETERMINATE:
      return item.reconciliationStatus === ReconciliationStatus.INDETERMINATE;
    case ReconciliationTab.ALL:
    default:
      return true;
  }
};

interface VersionExtractCellProps {
  label: string;
  content: string | null | undefined;
  blobName: string | null | undefined;
  pageNumber: number | null | undefined;
  selected?: boolean;
}

const VersionExtractCell: FC<VersionExtractCellProps> = ({
  label,
  content,
  blobName,
  pageNumber,
  selected = false,
}) => (
  <>
    <span
      className={clsx(
        'mb-1 block text-xs font-semibold uppercase tracking-wider',
        selected ? 'text-day-light-2' : 'text-gray-400',
      )}
    >
      {label}
    </span>
    {content ? (
      <blockquote
        className={clsx(
          'border-l-2 pl-2 text-sm italic line-clamp-4 break-words',
          selected ? 'border-day-light-2 text-neutral-on-brand' : 'border-gray-300 text-gray-700',
        )}
      >
        &ldquo;{content}&rdquo;
      </blockquote>
    ) : (
      <span className={selected ? 'text-day-light-2' : 'text-gray-400'}>—</span>
    )}
    {blobName && (
      <p
        className={clsx(
          'mt-2 text-xs uppercase tracking-wide break-words',
          selected ? 'text-day-light-2' : 'text-gray-400',
        )}
      >
        {blobName}
        {pageNumber != null ? ` · P.${pageNumber + 1}` : ''}
      </p>
    )}
  </>
);

interface SubGroupBlockProps {
  subGroup: ContentSubGroup;
  hasComments: boolean;
  isFirst: boolean;
  selectedId: number | null;
  expanded: boolean;
  onToggle: (key: string) => void;
  onSelect: (id: number) => void;
  onPrefetch: (id: number) => void;
  isNavigable: (item: VersionComparison) => boolean;
  registerRow: (id: number) => (node: HTMLElement | null) => void;
  onReviewChange: (id: number, status: VersionCompareReviewStatus) => void;
}

// Renders one content cluster within a page group: a unique finding stays flat,
// while findings that share the same Version A / Version B extract collapse into
// a nested dropdown that shows the extract once and lists each change inside.
const SubGroupBlock: FC<SubGroupBlockProps> = ({
  subGroup,
  hasComments,
  isFirst,
  selectedId,
  expanded,
  onToggle,
  onSelect,
  onPrefetch,
  isNavigable,
  registerRow,
  onReviewChange,
}) => {
  const { t } = useTranslation();
  const colSpanAll = hasComments ? 5 : 3;

  const spacer = !isFirst && (
    <tr aria-hidden="true">
      <td colSpan={colSpanAll} className="p-0">
        <div className="h-2 bg-day-light-3" />
      </td>
    </tr>
  );

  if (subGroup.findings.length === 1) {
    const item = subGroup.findings[0];
    const descriptionText = item.changeDescription;
    const navigable = isNavigable(item);
    const selected = selectedId === item.id;
    return (
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
      <tbody
        ref={registerRow(item.id)}
        role={navigable ? 'button' : undefined}
        tabIndex={navigable ? 0 : undefined}
        onClick={navigable ? () => onSelect(item.id) : undefined}
        onMouseEnter={navigable ? () => onPrefetch(item.id) : undefined}
        onFocus={navigable ? () => onPrefetch(item.id) : undefined}
        onKeyDown={
          navigable
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelect(item.id);
                }
              }
            : undefined
        }
        className={clsx(
          navigable && 'cursor-pointer',
          selected ? 'bg-day-dark-1' : 'hover:bg-day-light-4',
        )}
      >
        {spacer}
        <tr>
          <td colSpan={2} className="border-t border-gray-100 px-4 pb-1 pt-4 align-top">
            <span
              className={clsx(
                'break-words text-sm font-semibold',
                selected ? 'text-neutral-on-brand' : 'text-gray-700',
              )}
            >
              {t('version-compare.reconciliation.change-description-label')} · {descriptionText}
            </span>
          </td>
          {hasComments && (
            <>
              <td rowSpan={2} className="border-l border-t border-gray-100 px-4 py-4 align-top">
                {item.commentText ? (
                  <p
                    className={clsx(
                      'break-words text-sm',
                      selected ? 'text-neutral-on-brand' : 'text-gray-700',
                    )}
                  >
                    {item.commentText}
                  </p>
                ) : (
                  <span
                    className={clsx('text-sm', selected ? 'text-day-light-2' : 'text-gray-400')}
                  >
                    —
                  </span>
                )}
              </td>
              <td rowSpan={2} className="border-t border-gray-100 px-4 py-4 align-top">
                <ReconciliationBadge
                  status={item.reconciliationStatus}
                  confidence={item.reconciliationConfidence}
                  selected={selected}
                />
              </td>
            </>
          )}
          <td rowSpan={2} className="border-t border-gray-100 px-4 py-4 align-top">
            <ReviewCell
              id={item.id}
              value={item.reviewStatus}
              onChange={(status) => onReviewChange(item.id, status)}
            />
          </td>
        </tr>
        <tr>
          <td className="px-4 pb-4 pt-1 align-top">
            <VersionExtractCell
              label={t('version-compare.reconciliation.label-version-a')}
              content={item.content}
              blobName={item.blobName}
              pageNumber={item.pageNumber}
              selected={selected}
            />
          </td>
          <td className="px-4 pb-4 pt-1 align-top">
            <VersionExtractCell
              label={t('version-compare.reconciliation.label-version-b')}
              content={item.refContent}
              blobName={item.refBlobName}
              pageNumber={item.refPageNumber}
              selected={selected}
            />
          </td>
        </tr>
      </tbody>
    );
  }

  const ChevronIcon = expanded ? HiChevronDown : HiChevronRight;

  return (
    <tbody>
      {spacer}
      <tr>
        <td className="border-t border-gray-100 px-4 pb-3 pt-3 pl-8 align-top">
          <VersionExtractCell
            label={t('version-compare.reconciliation.label-version-a')}
            content={subGroup.content}
            blobName={subGroup.blobName}
            pageNumber={subGroup.pageNumber}
          />
        </td>
        <td className="border-t border-gray-100 px-4 pb-3 pt-3 align-top">
          <VersionExtractCell
            label={t('version-compare.reconciliation.label-version-b')}
            content={subGroup.refContent}
            blobName={subGroup.refBlobName}
            pageNumber={subGroup.refPageNumber}
          />
        </td>
        <td
          colSpan={hasComments ? 3 : 1}
          className={clsx('border-t border-gray-100', hasComments && 'border-l')}
        />
      </tr>
      <tr>
        <td colSpan={colSpanAll} className="border-t border-gray-100 p-0">
          <button
            type="button"
            onClick={() => onToggle(subGroup.key)}
            aria-expanded={expanded}
            className="flex w-full items-center gap-2 bg-day-light-2 px-4 py-2 pl-8 text-left hover:bg-day-light-1"
          >
            <ChevronIcon className="shrink-0 text-gray-400" size={16} />
            <span className="flex-1 min-w-0 text-sm font-medium text-gray-600">
              {t('version-compare.reconciliation.subgroup-toggle', {
                count: subGroup.findings.length,
              })}
            </span>
          </button>
        </td>
      </tr>
      {expanded &&
        subGroup.findings.map((item) => {
          const descriptionText = item.changeDescription;
          const navigable = isNavigable(item);
          const selected = selectedId === item.id;
          return (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
            <tr
              key={item.id}
              ref={registerRow(item.id)}
              role={navigable ? 'button' : undefined}
              tabIndex={navigable ? 0 : undefined}
              onClick={navigable ? () => onSelect(item.id) : undefined}
              onMouseEnter={navigable ? () => onPrefetch(item.id) : undefined}
              onFocus={navigable ? () => onPrefetch(item.id) : undefined}
              onKeyDown={
                navigable
                  ? (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onSelect(item.id);
                      }
                    }
                  : undefined
              }
              className={clsx(
                navigable && 'cursor-pointer',
                selected ? 'bg-day-dark-1' : 'hover:bg-day-light-4',
              )}
            >
              <td colSpan={2} className="border-t border-gray-100 px-4 py-3 pl-8 align-top">
                <span
                  className={clsx(
                    'break-words text-sm font-semibold',
                    selected ? 'text-neutral-on-brand' : 'text-gray-700',
                  )}
                >
                  {t('version-compare.reconciliation.change-description-label')} · {descriptionText}
                </span>
              </td>
              {hasComments && (
                <>
                  <td className="border-l border-t border-gray-100 px-4 py-3 align-top">
                    {item.commentText ? (
                      <p
                        className={clsx(
                          'break-words text-sm',
                          selected ? 'text-neutral-on-brand' : 'text-gray-700',
                        )}
                      >
                        {item.commentText}
                      </p>
                    ) : (
                      <span
                        className={clsx('text-sm', selected ? 'text-day-light-2' : 'text-gray-400')}
                      >
                        —
                      </span>
                    )}
                  </td>
                  <td className="border-t border-gray-100 px-4 py-3 align-top">
                    <ReconciliationBadge
                      status={item.reconciliationStatus}
                      confidence={item.reconciliationConfidence}
                      selected={selected}
                    />
                  </td>
                </>
              )}
              <td className="border-t border-gray-100 px-4 py-3 align-top">
                <ReviewCell
                  id={item.id}
                  value={item.reviewStatus}
                  onChange={(status) => onReviewChange(item.id, status)}
                />
              </td>
            </tr>
          );
        })}
    </tbody>
  );
};

const VersionReconciliationTableContent: FC = () => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const packageId = usePackageId();
  const [activeTab, setActiveTab] = useState<ReconciliationTab>(ReconciliationTab.ALL);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selectedBlobOption, setSelectedBlobOption] = useState<SelectOption[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [expandedSubGroups, setExpandedSubGroups] = useState<Set<string>>(new Set());
  const [hideReviewed, setHideReviewed] = useState(false);

  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>();
  const [updateReview] = useUpdateVersionComparisonReviewMutation();

  // Row elements keyed by finding id, so navigating the preview panel can bring the
  // matching row into view. hasScrolledRef keeps the first scroll instant and
  // animates every subsequent one.
  const rowRefs = useRef<Map<number, HTMLElement>>(new Map());
  const hasScrolledRef = useRef(false);

  const registerRow = useCallback(
    (id: number) => (node: HTMLElement | null) => {
      if (node) rowRefs.current.set(id, node);
      else rowRefs.current.delete(id);
    },
    [],
  );

  const { pkg } = usePackage(projectId, packageId);
  const [exportToExcel, { isLoading: isExporting }] = useExportToExcelMutation();

  // Debounce search and reset to first page when it settles.
  useEffect(() => {
    const handle = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
      setSelectedId(null);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const relatedBlobIds = selectedBlobOption.length
    ? String(selectedBlobOption[0].value)
    : undefined;

  // Server filters: search + document only — tab filtering is done client-side.
  const serverFilters = useMemo(
    () => ({
      search: search || undefined,
      relatedBlobIds,
    }),
    [search, relatedBlobIds],
  );

  // Fetch the full filtered set once; tab switching is pure client-side. The memoized args
  // double as the RTK Query cache key for the optimistic review patch below, so both must
  // reference the exact same object shape.
  const versionComparisonsArgs = useMemo(
    () => ({ projectId, packageId, page: 'all' as const, ...serverFilters }),
    [projectId, packageId, serverFilters],
  );

  const { comparisons, isLoading, isFetching } = useVersionComparisons(versionComparisonsArgs);

  // Optimistically flip the row in the cached list so the checkbox/badges update instantly,
  // then reconcile: undo the patch and surface a toast if the PATCH fails.
  const handleReviewChange = useCallback(
    async (id: number, reviewStatus: VersionCompareReviewStatus) => {
      const patch = dispatch(
        apiUtil.updateQueryData(
          'getVersionComparisons',
          versionComparisonsArgs,
          (draft: GetVersionComparisonsResponse) => {
            const target = draft.results.find((row) => row.id === id);
            if (target) target.reviewStatus = reviewStatus;
          },
        ),
      );
      try {
        await updateReview({ projectId, packageId, id, reviewStatus }).unwrap();
      } catch {
        patch.undo();
        toast.error(t('version-compare.reconciliation.review-update-error'));
      }
    },
    [dispatch, versionComparisonsArgs, updateReview, projectId, packageId, t],
  );

  const filteredComparisons = useMemo(
    () =>
      comparisons.filter(
        (item) =>
          applyTabFilter(item, activeTab) &&
          (!hideReviewed || item.reviewStatus === VersionCompareReviewStatus.UNREVIEWED),
      ),
    [comparisons, activeTab, hideReviewed],
  );

  const unreviewedCount = useMemo(
    () =>
      comparisons.filter((item) => item.reviewStatus === VersionCompareReviewStatus.UNREVIEWED)
        .length,
    [comparisons],
  );

  const groups = useMemo(() => groupComparisonsByPage(filteredComparisons), [filteredComparisons]);

  const orderedFindings = useMemo(() => groups.flatMap((g) => g.findings), [groups]);

  // Maps each finding to its page group, that group's index (to derive the
  // pagination page) and, when it lives in a multi-finding cluster, its sub-group,
  // so selecting a finding can page to and reveal it.
  const findingLocation = useMemo(() => {
    const map = new Map<
      number,
      { groupKey: string; groupIndex: number; subGroupKey: string | null }
    >();
    groups.forEach((group, groupIndex) => {
      for (const subGroup of group.subGroups) {
        const subGroupKey = subGroup.findings.length > 1 ? subGroup.key : null;
        for (const finding of subGroup.findings) {
          map.set(finding.id, { groupKey: group.key, groupIndex, subGroupKey });
        }
      }
    });
    return map;
  }, [groups]);

  const visibleGroups = useMemo(
    () => groups.slice((page - 1) * GROUPS_PER_PAGE, page * GROUPS_PER_PAGE),
    [groups, page],
  );

  const isNavigable = useCallback(
    (item: VersionComparison) => Boolean(item.blobId || item.refBlobId),
    [],
  );

  const selectedComparison = useMemo(
    () => (selectedId != null ? (orderedFindings.find((f) => f.id === selectedId) ?? null) : null),
    [orderedFindings, selectedId],
  );

  const selectedOrdIdx = useMemo(
    () => (selectedId != null ? orderedFindings.findIndex((f) => f.id === selectedId) : -1),
    [orderedFindings, selectedId],
  );

  const hasPrevious = useMemo(
    () => selectedOrdIdx > 0 && orderedFindings.slice(0, selectedOrdIdx).some(isNavigable),
    [selectedOrdIdx, orderedFindings, isNavigable],
  );

  const hasNext = useMemo(
    () => selectedOrdIdx >= 0 && orderedFindings.slice(selectedOrdIdx + 1).some(isNavigable),
    [selectedOrdIdx, orderedFindings, isNavigable],
  );

  // On-demand detail prefetch, shared by neighbour warming and row hover. RTK
  // Query dedupes, so repeat calls for an already-cached finding are free.
  const prefetchDetail = useCallback(
    (id: number) => {
      dispatch(apiUtil.prefetch('getVersionComparison', { projectId, packageId, id }, {}));
    },
    [dispatch, projectId, packageId],
  );

  // The very first finding a user opens can't be neighbour-warmed, so its detail
  // round-trip is what makes that first panel open lag (later opens are already
  // cached, hence fluid). Warm it on hover/focus — debounced so rows the cursor
  // merely passes over on the way to the target don't each mint a signed URL.
  const hoverPrefetchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const prefetchOnHover = useCallback(
    (id: number) => {
      clearTimeout(hoverPrefetchTimer.current);
      hoverPrefetchTimer.current = setTimeout(() => prefetchDetail(id), 120);
    },
    [prefetchDetail],
  );
  useEffect(() => () => clearTimeout(hoverPrefetchTimer.current), []);

  // Signed read URLs are minted per finding by the detail endpoint, so a cold
  // next/prev click otherwise waits a full round-trip before the pane can start
  // loading. Warm the immediate navigable neighbours' detail as soon as a finding
  // opens, so stepping is bound by PDF render — not the URL fetch — like Bid Review.
  useEffect(() => {
    if (selectedOrdIdx < 0) return;
    const neighbourIds: number[] = [];
    for (let i = selectedOrdIdx + 1; i < orderedFindings.length; i += 1) {
      if (isNavigable(orderedFindings[i])) {
        neighbourIds.push(orderedFindings[i].id);
        break;
      }
    }
    for (let i = selectedOrdIdx - 1; i >= 0; i -= 1) {
      if (isNavigable(orderedFindings[i])) {
        neighbourIds.push(orderedFindings[i].id);
        break;
      }
    }
    for (const id of neighbourIds) {
      prefetchDetail(id);
    }
  }, [selectedOrdIdx, orderedFindings, isNavigable, prefetchDetail]);

  const handlePrevious = useCallback(() => {
    setSelectedId((currentId) => {
      if (currentId == null) return currentId;
      const idx = orderedFindings.findIndex((f) => f.id === currentId);
      for (let i = idx - 1; i >= 0; i -= 1) {
        if (isNavigable(orderedFindings[i])) return orderedFindings[i].id;
      }
      return currentId;
    });
  }, [orderedFindings, isNavigable]);

  const handleNext = useCallback(() => {
    setSelectedId((currentId) => {
      if (currentId == null) return currentId;
      const idx = orderedFindings.findIndex((f) => f.id === currentId);
      for (let i = idx + 1; i < orderedFindings.length; i += 1) {
        if (isNavigable(orderedFindings[i])) return orderedFindings[i].id;
      }
      return currentId;
    });
  }, [orderedFindings, isNavigable]);

  // Tab counts come from the summary aggregate, scoped to the search / document filter.
  const { data: summary, isFetching: isFetchingCounts } = useGetVersionComparisonsSummaryQuery({
    projectId,
    packageId,
    search: search || undefined,
    relatedBlobIds,
  });

  // The summary has no comment-less aggregate, so it is derived from the fetched set — which is
  // scoped by the same server filters as the summary, keeping the count consistent with the others.
  const noCommentsCount = useMemo(
    () => comparisons.filter((item) => !item.commentText?.trim()).length,
    [comparisons],
  );

  // Whether this analysis has any comments at all (package-level, ignoring active filters).
  const { data: packageSummary } = useGetVersionComparisonsSummaryQuery({ projectId, packageId });
  const hasComments = (packageSummary?.totalComments ?? 0) > 0;

  const { data: uniqueBlobs = [] } = useGetVersionComparisonsUniqueBlobsQuery({
    projectId,
    packageId,
  });

  const blobOptions: SelectOption[] = useMemo(
    () => uniqueBlobs.map((blob) => ({ value: blob.id, label: blob.name })),
    [uniqueBlobs],
  );

  const handleTabChange = useCallback((tab: ReconciliationTab) => {
    setActiveTab(tab);
    setPage(1);
    setSelectedId(null);
  }, []);

  const handleBlobFilterChange = useCallback((options: SelectOption[]) => {
    setSelectedBlobOption(options);
    setPage(1);
    setSelectedId(null);
  }, []);

  const handleGroupToggle = useCallback((key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const handleSubGroupToggle = useCallback((key: string) => {
    setExpandedSubGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  // Reveal the selected finding: switch to its pagination page and expand its page
  // group and, when it sits inside a shared-content cluster, that cluster too (e.g.
  // while paging with next/previous, which can cross a pagination boundary).
  useEffect(() => {
    if (selectedId == null) return;
    const location = findingLocation.get(selectedId);
    if (!location) return;
    const targetPage = Math.floor(location.groupIndex / GROUPS_PER_PAGE) + 1;
    setPage((prev) => (prev === targetPage ? prev : targetPage));
    setCollapsedGroups((prev) => {
      if (!prev.has(location.groupKey)) return prev;
      const next = new Set(prev);
      next.delete(location.groupKey);
      return next;
    });
    if (location.subGroupKey !== null) {
      const subGroupKey = location.subGroupKey;
      setExpandedSubGroups((prev) => {
        if (prev.has(subGroupKey)) return prev;
        const next = new Set(prev);
        next.add(subGroupKey);
        return next;
      });
    }
  }, [selectedId, findingLocation]);

  // Follow the selection like a reviewing pane: once the finding's page/group is
  // revealed, bring its row into view. Depending on the reveal state (page and
  // expansion) re-runs this after the reveal commits, and the rAF waits for that
  // render to paint, so the freshly mounted row exists before we scroll to it.
  useEffect(() => {
    if (selectedId == null) {
      hasScrolledRef.current = false;
      return;
    }
    const smooth = hasScrolledRef.current;
    hasScrolledRef.current = true;
    const frame = requestAnimationFrame(() => {
      rowRefs.current.get(selectedId)?.scrollIntoView({
        behavior: smooth ? 'smooth' : 'auto',
        block: 'center',
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [selectedId, page, collapsedGroups, expandedSubGroups]);

  // Export reuses the in-memory tab-filtered set, avoiding a redundant network call.
  const handleExportExcel = useCallback(async () => {
    try {
      if (!filteredComparisons.length) {
        toast.error(t('version-compare.reconciliation.export-empty'));
        return;
      }
      const title = pkg?.name ?? t('version-compare.reconciliation.title');
      const excelExport = createVersionCompareExcelExport(filteredComparisons, t, title);
      const { base64Data } = await exportToExcel({ projectId, packageId, excelExport }).unwrap();
      saveBlobBase64(`${title}_version-compare.xlsx`, base64Data, MIME_TYPES.xlsx);
    } catch (error) {
      console.error('Error exporting version compare:', error);
      toast.error(t('version-compare.reconciliation.export-error'));
    }
  }, [filteredComparisons, pkg?.name, t, exportToExcel, projectId, packageId]);

  const tabs: { key: ReconciliationTab; label: string; count?: number }[] = [
    {
      key: ReconciliationTab.ALL,
      label: t('version-compare.reconciliation.tab-all'),
      count: summary?.totalChanges,
    },
    {
      key: ReconciliationTab.COMMENTS,
      label: t('version-compare.reconciliation.tab-comments'),
      count: summary?.totalComments,
    },
    {
      key: ReconciliationTab.NO_COMMENTS,
      label: t('version-compare.reconciliation.tab-no-comments'),
      count: noCommentsCount,
    },
    {
      key: ReconciliationTab.ADDRESSED,
      label: t('version-compare.reconciliation.tab-addressed'),
      count: summary?.totalAddressed,
    },
    {
      key: ReconciliationTab.UNADDRESSED,
      label: t('version-compare.reconciliation.tab-unaddressed'),
      count: summary?.totalUnaddressed,
    },
    {
      key: ReconciliationTab.INDETERMINATE,
      label: t('version-compare.reconciliation.tab-indeterminate'),
      count: summary?.totalIndeterminate,
    },
  ];

  const visibleTabs = hasComments ? tabs : tabs.filter((tab) => !COMMENT_RELATED_TABS.has(tab.key));

  if (isLoading && comparisons.length === 0) {
    return <LoadingArea title={t('document-list.loading')} />;
  }

  return (
    <div className="flex flex-col gap-4 px-6 py-4 w-full">
      <div className="flex items-start gap-2 rounded-md border border-gray-300 bg-day-light-3 px-4 py-3 text-sm text-day-neutral-dark">
        <HiInformationCircle className="mt-0.5 shrink-0 text-day-neutral-subtle" size={16} />
        <span>{t('version-compare.reconciliation.disclaimer')}</span>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-0.5">
          <h1 className="text-xl font-bold text-gray-900">
            {t('version-compare.reconciliation.title')}
          </h1>
          <span className="text-sm font-medium text-gray-500">
            {t('version-compare.reconciliation.findings-count', {
              shown: filteredComparisons.length,
              total: comparisons.length,
            })}
          </span>
          <span className="text-sm font-medium text-gray-500">
            {t('version-compare.reconciliation.unreviewed-count', { count: unreviewedCount })}
          </span>
        </div>
        <Button
          rounded
          secondary
          disabled={isExporting}
          onClick={handleExportExcel}
          icon={isExporting ? () => <Spinner size="xsmall" /> : undefined}
          className="text-sm font-bold min-h-[36px] whitespace-normal"
        >
          {t('version-compare.reconciliation.export-excel')}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {visibleTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => handleTabChange(tab.key)}
            className={
              activeTab === tab.key
                ? 'rounded-full bg-day-neutral-dark px-3 py-1 text-xs font-medium text-white transition-colors'
                : 'rounded-full bg-day-light-3 px-3 py-1 text-xs font-medium text-day-neutral-subtle transition-colors hover:bg-day-light-4'
            }
          >
            {tab.label}
            {!isFetchingCounts && tab.count !== undefined && ` (${tab.count})`}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <Search
          text={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={t('version-compare.reconciliation.search-placeholder')}
          className="flex-1"
        />
        <div className="w-64">
          <CustomSelect
            options={blobOptions}
            value={selectedBlobOption}
            onChange={handleBlobFilterChange}
            placeholder={t('version-compare.reconciliation.filter-by-document')}
            isClearable
          />
        </div>
        {/*[&_label] tightens the facade Checkbox's default label gap (fc:mx-4). */}
        <div className="ml-auto [&_label]:ml-2!">
          <Checkbox
            id="hide-reviewed"
            checked={hideReviewed}
            onChange={(event) => setHideReviewed(event.target.checked)}
            label={t('version-compare.reconciliation.hide-reviewed')}
          />
        </div>
      </div>

      {isFetching ? (
        <div className="flex h-32 items-center justify-center">
          <Spinner />
        </div>
      ) : groups.length === 0 ? (
        <div className="flex h-32 items-center justify-center rounded-md border border-gray-200 bg-white text-sm text-gray-500">
          {t('version-compare.reconciliation.no-findings')}
        </div>
      ) : (
        <div className="rounded-md border border-gray-200 bg-white shadow-sm">
          <table className="w-full table-fixed border-collapse text-sm">
            <colgroup>
              {hasComments ? (
                <>
                  <col className="w-[22%]" />
                  <col className="w-[22%]" />
                  <col className="w-[29%]" />
                  <col className="w-[15%]" />
                  <col className="w-[12%]" />
                </>
              ) : (
                <>
                  <col className="w-[44%]" />
                  <col className="w-[44%]" />
                  <col className="w-[12%]" />
                </>
              )}
            </colgroup>
            {/* Two stacked header rows pin under the sticky page header (h-14 = top-14) so
                they stay visible while the document scrolls; row two clears row one's fixed
                height on top of that (14 + h-10 = top-24). border-collapse drops a stuck
                cell's real border, so the dividers are inset box-shadows, not border-b. */}
            <thead>
              <tr className="h-10 bg-day-light-3">
                <th
                  colSpan={2}
                  className="sticky top-14 z-20 bg-day-light-3 px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-900 shadow-[inset_0_-1px_0_#e5e7eb]"
                >
                  {t('version-compare.reconciliation.col-changes-analysis')}
                </th>
                {hasComments && (
                  <th
                    colSpan={2}
                    className="sticky top-14 z-20 border-l border-gray-200 bg-day-light-3 px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-900 shadow-[inset_0_-1px_0_#e5e7eb]"
                  >
                    {t('version-compare.reconciliation.col-comment-analysis')}
                  </th>
                )}
                <th
                  rowSpan={2}
                  className="sticky top-14 z-20 border-l border-gray-200 bg-day-light-3 px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-900 shadow-[inset_0_-1px_0_#e5e7eb]"
                >
                  {t('version-compare.reconciliation.col-review')}
                </th>
              </tr>
              <tr className="bg-day-light-3">
                <th className="sticky top-24 z-20 break-words bg-day-light-3 px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 shadow-[inset_0_-1px_0_#e5e7eb]">
                  {t('version-compare.reconciliation.col-version-a')}
                </th>
                <th className="sticky top-24 z-20 break-words bg-day-light-3 px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 shadow-[inset_0_-1px_0_#e5e7eb]">
                  {t('version-compare.reconciliation.col-version-b')}
                </th>
                {hasComments && (
                  <>
                    <th className="sticky top-24 z-20 break-words border-l border-gray-200 bg-day-light-3 px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 shadow-[inset_0_-1px_0_#e5e7eb]">
                      {t('version-compare.reconciliation.col-comment-text')}
                    </th>
                    <th className="sticky top-24 z-20 break-words bg-day-light-3 px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 shadow-[inset_0_-1px_0_#e5e7eb]">
                      {t('version-compare.reconciliation.col-reconciliation')}
                    </th>
                  </>
                )}
              </tr>
            </thead>
            {visibleGroups.map((group) => {
              const isCollapsed = collapsedGroups.has(group.key);
              const ChevronIcon = isCollapsed ? HiChevronRight : HiChevronDown;
              const pageLabel = group.pageNumber != null ? ` · p.${group.pageNumber + 1}` : '';
              const docLabel =
                group.blobName ?? t('version-compare.reconciliation.group-no-document');
              const reviewedCount = group.findings.filter(
                (finding) => finding.reviewStatus !== VersionCompareReviewStatus.UNREVIEWED,
              ).length;
              return (
                <Fragment key={group.key}>
                  <tbody>
                    <tr>
                      <td colSpan={hasComments ? 5 : 3} className="border-t border-gray-200 p-0">
                        <button
                          type="button"
                          onClick={() => handleGroupToggle(group.key)}
                          aria-expanded={!isCollapsed}
                          className="flex w-full items-center gap-2 bg-day-light-3 px-4 py-3 text-left hover:bg-day-light-4"
                        >
                          <ChevronIcon className="shrink-0 text-gray-500" size={16} />
                          <span className="flex-1 min-w-0 truncate text-sm font-medium text-gray-700">
                            {docLabel}
                            {pageLabel}
                            {group.matchConfidence != null && (
                              <span className="ml-2 text-xs font-normal text-gray-500">
                                (
                                {t('version-compare.reconciliation.group-match-confidence', {
                                  value: group.matchConfidence,
                                })}
                                )
                              </span>
                            )}
                          </span>
                          <span className="shrink-0 rounded-full border border-gray-300 bg-white px-2 py-0.5 text-xs font-medium text-gray-600">
                            {t('version-compare.reconciliation.group-reviewed-count', {
                              reviewed: reviewedCount,
                              total: group.findings.length,
                            })}
                          </span>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                  {!isCollapsed &&
                    group.subGroups.map((subGroup, subGroupIndex) => (
                      <SubGroupBlock
                        key={subGroup.key}
                        subGroup={subGroup}
                        hasComments={hasComments}
                        isFirst={subGroupIndex === 0}
                        selectedId={selectedId}
                        expanded={expandedSubGroups.has(subGroup.key)}
                        onToggle={handleSubGroupToggle}
                        onSelect={setSelectedId}
                        onPrefetch={prefetchOnHover}
                        isNavigable={isNavigable}
                        registerRow={registerRow}
                        onReviewChange={handleReviewChange}
                      />
                    ))}
                </Fragment>
              );
            })}
          </table>
        </div>
      )}

      {groups.length > GROUPS_PER_PAGE && (
        <div className="flex justify-center pt-2">
          <Pagination
            currentPage={page}
            totalCount={groups.length}
            pageSize={GROUPS_PER_PAGE}
            onPageChange={(newPage) => {
              setPage(newPage);
              setSelectedId(null);
            }}
          />
        </div>
      )}

      {selectedComparison && (
        <VersionComparePdfPanel
          comparison={selectedComparison}
          projectId={projectId}
          packageId={packageId}
          onClose={() => setSelectedId(null)}
          onNext={handleNext}
          onPrevious={handlePrevious}
          hasNext={Boolean(hasNext)}
          hasPrevious={Boolean(hasPrevious)}
          onReviewChange={handleReviewChange}
        />
      )}
    </div>
  );
};

export const VersionReconciliationTable: FC = () => {
  const { t } = useTranslation();

  return (
    <WorkspaceProvider>
      <WorkspaceLayout>
        <Breadcrumb label={t('servicetabs.findings')} />
        <VersionReconciliationTableContent />
      </WorkspaceLayout>
    </WorkspaceProvider>
  );
};
